import { useState, useRef, useEffect } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Send } from "lucide-react";

const MODES = ["basic", "moderate", "advanced"];

export default function KadanaCopilot({ height = 560, endpoint = "/copilot" }) {
  const [mode, setMode] = useState("moderate");
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    { sender: "bot", mode: "moderate",
      text: "Hello! I am the Kadana Copilot. Ask me anything about building apps, APIs, local compliance mechanics, or integrations." },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, busy]);

  const send = async () => {
    const q = input.trim();
    if (!q || busy) return;
    setMessages((m) => [...m, { sender: "user", text: q }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, { query: q, mode });
      setMessages((m) => [...m, { sender: "bot", mode: data.mode, text: data.reply }]);
    } catch (e) {
      toast.error(errMsg(e));
      setMessages((m) => [...m, { sender: "bot", mode, text: "Sorry — I couldn't answer that just now. If this keeps happening, contact info@kadana.app." }]);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="kadana-copilot" style={{ display: "flex", flexDirection: "column", height,
      background: "var(--canvas)", border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden" }}>
      {/* Header + mode toggle */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        padding: "14px 18px", background: "var(--container)", borderBottom: "1px solid var(--border)", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 9, height: 9, borderRadius: 999, background: "var(--accent-primary)", display: "inline-block" }} className="k-fadein" />
          <span className="k-heading" style={{ fontWeight: 700, fontSize: 14, letterSpacing: ".06em", textTransform: "uppercase" }}>Kadana Copilot</span>
          <span className="k-muted mono" style={{ fontSize: 11 }}>v2026.3</span>
        </div>
        <div style={{ display: "flex", gap: 4, padding: 4, borderRadius: 10, background: "var(--canvas)", border: "1px solid var(--border)" }}>
          {MODES.map((m) => (
            <button key={m} data-testid={`copilot-mode-${m}`} onClick={() => setMode(m)}
              style={{ padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
                textTransform: "capitalize", border: "none", transition: "all .15s cubic-bezier(.4,0,.2,1)",
                background: mode === m ? "var(--accent-primary)" : "transparent",
                color: mode === m ? "#111113" : "var(--muted)" }}>
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} style={{ flex: 1, padding: 18, overflowY: "auto", display: "flex", flexDirection: "column", gap: 14, background: "var(--canvas)" }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: "flex", justifyContent: msg.sender === "user" ? "flex-end" : "flex-start" }}>
            <div data-testid={`copilot-msg-${msg.sender}`} style={{ maxWidth: "80%", padding: 14, borderRadius: 12, lineHeight: 1.6,
              background: msg.sender === "user" ? "var(--control)" : "var(--container)",
              color: msg.sender === "user" ? "var(--heading)" : "var(--muted)",
              border: `1px solid ${msg.sender === "user" ? "var(--border-strong)" : "var(--border)"}` }}>
              {msg.sender === "bot" && (
                <div className="mono" style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center",
                  fontSize: 10, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--muted)",
                  borderBottom: "1px solid var(--border)", paddingBottom: 6, marginBottom: 8 }}>
                  <span>Kadana AI</span>
                  {msg.mode && <span style={{ padding: "2px 6px", borderRadius: 6, background: "var(--control)", color: "var(--heading)" }}>{msg.mode} mode</span>}
                </div>
              )}
              <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{msg.text}</div>
            </div>
          </div>
        ))}
        {busy && <div className="k-muted" style={{ fontSize: 12 }} data-testid="copilot-thinking">Kadana Copilot is thinking…</div>}
      </div>

      {/* Input */}
      <div style={{ display: "flex", gap: 10, padding: 14, background: "var(--container)", borderTop: "1px solid var(--border)" }}>
        <input className="k-control" data-testid="copilot-input" value={input} disabled={busy}
          onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={`Ask Kadana Copilot (${mode.toUpperCase()} mode active)…`} />
        <button className="k-btn k-btn-primary" data-testid="copilot-send" onClick={send} disabled={busy}>
          <Send size={15} /> Send
        </button>
      </div>
    </div>
  );
}
